import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { formatAmount } from '@/lib/venue/format'
import type { VenueRevenueSummary } from '@/lib/venue/revenueTypes'
import { amountTextClass, feeMissingNoticeText, onsitePaidNoticeText } from '@/lib/venue/revenueViewModel'

/**
 * 매출 현황 요약 카드 4종(설계 §2) — 총 예약액/취소·환불/적용 수수료/순매출, 카드마다 산출 기준
 * 보조 문구를 함께 표시한다(이슈 요구사항 2).
 *
 * 🚨 `/revenues`(예약 축) 전용이다 — `SettlementRevenueSummaryCard`(회차 축, `/settlements/revenue`)와
 * 절대 같은 컴포넌트를 쓰지 않는다(api-spec §0-① "두 축의 숫자를 비교·연산하지 않는다"는 규약을
 * 컴포넌트 분리로 강제한다).
 */
export function RevenueSummaryCards({ summary }: { summary: VenueRevenueSummary }) {
  const feeMissingNotice = feeMissingNoticeText(summary)
  const onsitePaidNotice = onsitePaidNoticeText(summary)

  return (
    <Card>
      <CardHeader title="매출 요약" description="결제 시점에 적용된 수수료 기준으로 집계합니다." />
      <CardBody>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-xs text-slateish-600">총 예약액</dt>
            <dd className="mt-1 text-lg font-semibold text-ink-700">
              {formatAmount(summary.reservationAmount)}
            </dd>
            <p className="mt-0.5 text-xs text-slateish-400">예약 결제 합계</p>
          </div>
          <div>
            <dt className="text-xs text-slateish-600">취소·환불</dt>
            <dd className="mt-1 text-lg font-semibold text-danger-500">
              -{formatAmount(summary.refundedAmount)}
            </dd>
            <p className="mt-0.5 text-xs text-slateish-400">취소 건 환불 합계</p>
          </div>
          <div>
            <dt className="text-xs text-slateish-600">적용 수수료</dt>
            <dd className="mt-1 text-lg font-semibold text-ink-700">
              -{formatAmount(summary.appliedFeeAmount)}
            </dd>
            <p className="mt-0.5 text-xs text-slateish-400">결제 시점 수수료 기준</p>
          </div>
          <div>
            <dt className="text-xs text-slateish-600">순매출</dt>
            <dd className={`mt-1 text-lg font-semibold ${amountTextClass(summary.netRevenueAmount)}`}>
              {formatAmount(summary.netRevenueAmount)}
            </dd>
            <p className="mt-0.5 text-xs text-slateish-400">예약액 − 취소 − 수수료</p>
          </div>
        </dl>

        {feeMissingNotice ? <p className="mt-4 text-xs text-warning-500">{feeMissingNotice}</p> : null}
        {onsitePaidNotice ? <p className="mt-1 text-xs text-slateish-400">{onsitePaidNotice}</p> : null}
      </CardBody>
    </Card>
  )
}
